/** Steps shown in the unified story popup. Same order as the plan card. */

export const UNIFIED_PIPELINE_STEPS = [
  { id: 'input', label: 'Input' },
  { id: 'choices', label: 'Choose format, script, voice, and character views' },
  { id: 'screenplay', label: 'Screenplay' },
  { id: 'generate', label: 'Generate' },
];

/** Suggested answers for the confirm step. Values match the create-agent catalogs. */
export const STORY_FORMAT_OPTIONS = [
  { value: 'short film', label: 'Short film' },
  { value: 'lesson', label: 'Lesson' },
  { value: 'advertisement', label: 'Advertisement' },
  { value: 'news bulletin', label: 'News bulletin' },
  { value: 'documentary', label: 'Documentary' },
  { value: 'hybrid', label: 'Hybrid' },
];

export const DIALOGUE_FORMAT_OPTIONS = [
  { value: 'dialogue', label: 'Dialogue' },
  { value: 'dialogue with narration', label: 'Dialogue with narration' },
];

export const NARRATION_FORMAT_OPTION = { value: 'narration', label: 'Narration' };

const DIALOGUE_BASED = /\b(dialog(?:ue)?s?|conversations?|(?:talk|speak|chat)(?:ing)?\s+(?:to|with)\s+each\s+other|characters?\s+(?:talk|speak|chat|say|said|discuss|debate|argue)|argu(?:e|ing|ment)|संवाद|बातचीत)\b/i;

/** True when the request is a scene people speak, not a single-voice piece. */
export function isDialogueBased(text) {
  return DIALOGUE_BASED.test(String(text || ''));
}

const NON_STORY = /\b(lessons?|tutorials?|how[- ]tos?|explains?|explainers?|advertis\w*|commercials?|promos?|news|bulletins?|headlines?|documentar\w*|product demos?|announcements?)\b/i;
const STORY_CUE = /\b(stor(?:y|ies)|tales?|once upon|adventures?|bedtime|fables?|myths?|friends?|characters?|narrat\w*)\b/i;
const NAME_STOP = new Set([
  'The', 'This', 'That', 'Make', 'Create', 'Write', 'Explain', 'Once', 'When',
  'Short', 'Film', 'Story', 'Narration', 'Dialogue',
]);

/** True when the context is a story, not a lesson, ad, news piece, or documentary. */
export function contextIsStory(text) {
  const body = String(text || '');
  const storyCue = STORY_CUE.test(body) || isDialogueBased(body);
  if (NON_STORY.test(body) && !storyCue) return false;
  const names = (body.match(/\b[A-Z][a-z]{2,}\b/g) || []).filter((name) => !NAME_STOP.has(name));
  return storyCue || names.length > 0;
}

const STORY_TELLING_FORMATS = [
  ...DIALOGUE_FORMAT_OPTIONS,
  NARRATION_FORMAT_OPTION,
];

/**
 * A detected story offers dialogue, dialogue with narration, and narration.
 * Anything else offers the other video formats.
 */
export function formatOptionsForStory(text) {
  return contextIsStory(text) ? STORY_TELLING_FORMATS : STORY_FORMAT_OPTIONS;
}

export const STORY_LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'ta', label: 'Tamil' },
  { value: 'te', label: 'Telugu' },
  { value: 'bn', label: 'Bengali' },
  { value: 'mr', label: 'Marathi' },
];

export const STORY_TONE_OPTIONS = [
  { value: 'inspiring', label: 'Inspiring' },
  { value: 'dramatic', label: 'Dramatic' },
  { value: 'funny', label: 'Funny / light' },
  { value: 'calm', label: 'Calm / soothing' },
  { value: 'suspenseful', label: 'Suspenseful' },
  { value: 'informative', label: 'Informative' },
];

export const STORY_AUDIENCE_OPTIONS = [
  { value: 'general', label: 'General' },
  { value: 'kids', label: 'Kids / family' },
  { value: 'teens', label: 'Teens' },
  { value: 'professionals', label: 'Professionals' },
  { value: 'enterprise', label: 'Enterprise / B2B' },
];

const NON_CHARACTER_WORDS = new Set([
  'The', 'A', 'An', 'This', 'That', 'These', 'Those', 'And', 'But', 'For',
  'With', 'From', 'Into', 'Once', 'When', 'Where', 'What', 'Ignore', 'User',
  'Instructions', 'Story', 'Person', 'Make', 'Create', 'Write', 'Turn', 'Help',
  'Produce', 'Build', 'Generate', 'Explain', 'Upload', 'Show', 'Tell', 'Give',
  'Add', 'Use', 'Keep', 'Start', 'Open', 'Please', 'Let', 'Can', 'Could',
  'Would', 'Should', 'Will', 'Just', 'Also', 'Then', 'After', 'Before',
  'During', 'About', 'Your', 'Our', 'Their', 'His', 'Her', 'Its', 'My', 'We',
  'They', 'You', 'He', 'She', 'It', 'Is', 'Are', 'Was', 'Were', 'Have', 'Has',
  'Had', 'Do', 'Does', 'Did', 'Not', 'No', 'Yes', 'Or', 'If', 'So', 'As', 'At',
  'By', 'On', 'In', 'Of', 'To', 'Up', 'Out', 'Over', 'Here', 'There', 'How',
  'Why', 'Who', 'Which', 'Using', 'Film', 'Short', 'Video',
]);

const NOT_A_CHARACTER = new Set([
  'hindi', 'english', 'spanish', 'french', 'german', 'tamil', 'telugu', 'bengali', 'marathi',
  'pixar', 'disney', 'anime', 'ghibli', 'cinematic', 'photoreal', 'photorealistic',
  'reels', 'reel', 'shorts', 'youtube', 'tiktok', 'instagram', 'bedtime',
]);

const GROUP_LABEL = /^(?:two|three|four|five|several|some)\s+(?:friends|people|kids|children|boys|girls)$/i;

export function isCharacterName(name) {
  const word = String(name || '').trim();
  if (!word || NON_CHARACTER_WORDS.has(word) || NOT_A_CHARACTER.has(word.toLowerCase())) return false;
  return !GROUP_LABEL.test(word);
}

export const FORMAT_FIELD_OPTIONS = [
  NARRATION_FORMAT_OPTION,
  ...DIALOGUE_FORMAT_OPTIONS,
  ...STORY_FORMAT_OPTIONS,
];

export const FORMAT_LANGUAGE_OPTIONS = [
  { value: 'English', label: 'English' },
  { value: 'Hindi', label: 'Hindi' },
  { value: 'Spanish', label: 'Spanish' },
  { value: 'French', label: 'French' },
  { value: 'German', label: 'German' },
  { value: 'Tamil', label: 'Tamil' },
  { value: 'Telugu', label: 'Telugu' },
  { value: 'Bengali', label: 'Bengali' },
  { value: 'Marathi', label: 'Marathi' },
];

export const FORMAT_GENRE_OPTIONS = [
  'bedtime', 'mythology', 'adventure', 'comedy', 'drama', 'documentary', 'motivational',
  'romance', 'horror', 'educational', 'news', 'fantasy',
].map((value) => ({ value, label: value.charAt(0).toUpperCase() + value.slice(1) }));

export const FORMAT_SUBGENRE_OPTIONS = [
  'none noted', 'dark comedy', 'fairy tale', 'magical', 'bedtime', 'coming of age',
].map((value) => ({ value, label: value.charAt(0).toUpperCase() + value.slice(1) }));

export const FORMAT_TONE_OPTIONS = [
  ...STORY_TONE_OPTIONS,
  { value: 'warm', label: 'Warm' },
];

export const FORMAT_PERSPECTIVE_OPTIONS = [
  { value: 'First person', label: 'First person' },
  { value: 'Third person', label: 'Third person' },
  { value: 'Second person', label: 'Second person' },
];

export const FORMAT_BALANCE_OPTIONS = [
  { value: 'narration', label: 'Narration' },
  { value: 'dialogue', label: 'Dialogue' },
  { value: 'mixed', label: 'Dialogue and narration' },
];

export const FORMAT_VISUAL_OPTIONS = [
  { value: 'cinematic', label: 'Cinematic' },
  { value: 'animation', label: 'Animation' },
  { value: 'Pixar cinematic', label: 'Pixar cinematic' },
  { value: 'photorealistic', label: 'Photorealistic' },
  { value: 'watercolor', label: 'Watercolor' },
];

export const FORMAT_DIALOGUE_STYLE_OPTIONS = [
  { value: 'Natural spoken dialogue', label: 'Natural spoken dialogue' },
  { value: 'Spoken dialogue', label: 'Spoken dialogue' },
  { value: 'Documentary narration', label: 'Documentary narration' },
  { value: 'Voiceover', label: 'Voiceover' },
];

export const FORMAT_ADAPTATION_OPTIONS = [
  { value: 'creative', label: 'Creative' },
  { value: 'faithful', label: 'Faithful' },
];

export const FORMAT_CONSTRAINT_OPTIONS = [
  { value: 'None noted', label: 'None noted' },
  { value: 'Family-friendly', label: 'Family-friendly' },
  { value: 'Under 3 minutes', label: 'Under 3 minutes' },
  { value: 'No violence', label: 'No violence' },
];

export const FORMAT_DURATION_OPTIONS = [
  { value: '30 sec', label: '30 sec', seconds: 30 },
  { value: '45 sec', label: '45 sec', seconds: 45 },
  { value: '1 min', label: '1 min', seconds: 60 },
  { value: '90 sec', label: '90 sec', seconds: 90 },
  { value: '3 min', label: '3 min', seconds: 180 },
  { value: '5 min', label: '5 min', seconds: 300 },
];

export function optionsIncludingCurrent(options, current) {
  const value = String(current || '').trim();
  if (!value || options.some((option) => option.value === value)) return options;
  return [{ value, label: value }, ...options];
}

export const REFERENCE_FRAMES = [
  'faceFront',
  'faceBack',
  'faceLeft',
  'faceRight',
  'bodyFront',
  'bodyBack',
  'bodyLeft',
  'bodyRight',
];

const ACTIVE_INDEX = {
  collecting_input: 0,
  analyzing_sources: 0,
  context_review: 1,
  format_review: 1,
  format_locked: 1,
  character_review: 1,
  context_locked: 2,
  screenplay_review: 2,
  screenplay_locked: 3,
  media_generation: 3,
};

export function pipelineStepStatus(workflow, index) {
  const state = workflow?.state || 'collecting_input';
  if (state === 'failed') {
    if (index === 0) return 'done';
    if (index === 1) return 'failed';
    return 'pending';
  }
  const active = ACTIVE_INDEX[state] ?? 0;
  if (state === 'media_generation' && index === 3) {
    const beats = Object.values(workflow?.generation || {});
    const settled = beats.length > 0 && beats.every((beat) => (
      beat?.status === 'completed' || beat?.status === 'waiting_for_take' || beat?.media_url
    ));
    return settled ? 'done' : 'active';
  }
  if (index < active) return 'done';
  if (index === active) return 'active';
  return 'pending';
}

export const DETECTION_LIMITED_NOTICE = 'Detection was limited. The names below come from the text we could read. Correct them before you lock the context.';

export function detectionLimitedNotice(workflow) {
  return workflow?.detection_limited ? DETECTION_LIMITED_NOTICE : '';
}

export function spokenLines(beat) {
  const dialogue = (beat?.dialogue || []).map((line) => line?.text).filter(Boolean);
  const narration = (beat?.narration || []).map((line) => line?.text).filter(Boolean);
  return [...dialogue, ...narration];
}
